import { useState } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import {
  ArrowUturnDownIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { Card } from '../../../components/base/Card';
import { DataTable } from '../../../components/base/Table';
import { Button } from '../../../components/base/Button';
import { ConfirmDelete } from '../../../components/shared/ConfirmDelete';
import { IFaultAction } from '../../../types/Fault/FaultCause';
import useSuperAdminFaultActionMutations from '../../../data/fault/superadmin/useSuperAdminFaultActionMutations';
import { useClipboardCopy } from '../../../hooks/useClipboardCopy';
import { faultQueryKeys } from '../../../data/QueryKeys';
import CreateEditFaultAction from '../../../components/Fault/CreateEditFaultAction';

const colHelper = createColumnHelper<IFaultAction>();

export function FaultActionsPage() {
  const { CopyToClipboardButton } = useClipboardCopy();
  const { delFaultAction, updateFaultAction } =
    useSuperAdminFaultActionMutations();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteFaultActionItem, setDeleteFaultActionItem] = useState<
    IFaultAction | undefined
  >(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFaultAction, setSelectedFaultAction] = useState<
    IFaultAction | undefined
  >(undefined);

  const handleDeleteFaultAction = (faultAction: IFaultAction) => {
    setDeleteFaultActionItem(faultAction);
    setDeleteOpen(true);
  };

  const handleConfirmDeleteFaultAction = () => {
    delFaultAction.mutate(deleteFaultActionItem!);
    setDeleteOpen(false);
  };

  const handleRestoreFaultAction = (faultAction: IFaultAction) => {
    updateFaultAction.mutate({ ...faultAction, restore: true });
  };

  const handleEditFaultAction = (faultAction: IFaultAction) => {
    setSelectedFaultAction(faultAction);
    setModalOpen(true);
  };

  const handleAddFaultAction = () => {
    setSelectedFaultAction(undefined);
    setModalOpen(true);
  };

  const handleModalClose = (refresh?: boolean) => {
    setModalOpen(false);
    setSelectedFaultAction(undefined);
    if (refresh) {
      // The table will automatically refresh due to query invalidation in the mutation
    }
  };

  const cols = [
    colHelper.accessor('title', { header: 'Title' }),
    colHelper.accessor('description', {
      header: 'Description',
      cell: (info) => (
        <div className="max-w-xs truncate">{info.getValue()}</div>
      ),
    }),
    colHelper.display({
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          {info.cell.row.original.deletedOn ? (
            <ArrowUturnDownIcon
              className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
              onClick={() => handleRestoreFaultAction(info.row.original)}
            />
          ) : (
            <>
              <CopyToClipboardButton iconOnly copyText={info.row.original.id} />
              <PencilSquareIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleEditFaultAction(info.row.original)}
              />
              <TrashIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleDeleteFaultAction(info.row.original)}
              />
            </>
          )}
        </div>
      ),
    }),
  ];

  return (
    <>
      <ConfirmDelete
        itemName={'Fault Action'}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDeleteFaultAction()}
      />
      <CreateEditFaultAction
        open={modalOpen}
        onClose={handleModalClose}
        faultAction={selectedFaultAction}
      />
      <Card title="Fault Actions">
        <div className="my-6 space-x-4">
          <Button onClick={handleAddFaultAction}>Add Fault Action</Button>
        </div>
        <div>
          <DataTable<IFaultAction>
            url="/superadmin/fault/actions"
            queryKey={faultQueryKeys.superAdminActions()}
            columns={cols}
            enableShowDeleted
          />
        </div>
      </Card>
    </>
  );
}
